import { inject, markRaw } from 'vue';
import ModelRecordInspector from '@/components/ModelRecordInspector.vue';
import { entityDisplayNameFor, entityModel } from '@/composables/entityColumns';
import { INSPECTOR_HOST } from '@/composables/inspectorHostContext';
import { RECORD_PANE_NAVIGATOR } from '@/composables/recordPaneTrail';
import { overlayHeaderComponentFor } from '@/composables/searchInspectorRegistry';
import { useInspectorStore } from '@/stores/inspectorStore';

/**
 * Click-to-inspect for foreign-model values (summary relations, foreign-model
 * table cells, card-header names, id-cell side peeks). One shared path so the
 * semantics stay uniform:
 *  - inside the inspector (INSPECTOR_HOST provided) the record is push()ed —
 *    the back chevron returns to where the user came from;
 *  - on a list with a pane, inside the pane or in one of the list's rows, the
 *    pane moves to the record (RECORD_PANE_NAVIGATOR provided);
 *  - anywhere else it open()s as the root side-peek view.
 *
 * Guard with canOpen() first: entities absent from models.json have no columns
 * or slug to render an inspector from.
 */
export interface OpenRecordOptions {
    /**
     * Push instead of replacing when the inspector is open, for a control that
     * sits OUTSIDE the inspector but on top of it.
     *
     * The player bar is the case this exists for. It is not inside the
     * inspector, but the phone's sheet is deliberately sized to leave it
     * visible below, so on a phone you can be several levels deep, tap the artist line, and land on the
     * Artist record — with `open()` that resets the stack and Back goes dead,
     * which is the very complaint this branch set out to fix.
     *
     * Table cells keep the default: stacking unrelated rows there would make
     * Back a surprise tour of everything you happened to click.
     */
    pushWhenInspectorOpen?: boolean;
    /**
     * Which section the entity's inspector should land on, for a caller opening
     * a record to show one particular thing.
     *
     * The track card's lyrics chip is the case: it opens a Live Track ON its
     * Lyrics view rather than on Details with the user left to find it.
     *
     * Forwarded blindly to the inspector. Entities whose inspector has no
     * sections ignore the prop, and one that does not offer the named section
     * falls back to its default — so this is a hint, never a contract.
     */
    initialSection?: string | null;
    /**
     * The name of the record the link sits inside, for the start of the peek's
     * trail. Only a new stack uses it: a pushed peek keeps the stack's start.
     */
    originTitle?: string;
}

export function useOpenRecordInInspector() {
    const inspectorStore = useInspectorStore();
    const isInsideInspector = inject(INSPECTOR_HOST, false);
    // A list's pane, or a row of a list that has one. Spec: record-navigation-layers.md.
    const paneNavigator = inject(RECORD_PANE_NAVIGATOR, null);

    function canOpen(entityId: string | null | undefined): entityId is string {
        return typeof entityId === 'string' && entityId !== '' && entityModel(entityId) !== undefined;
    }

    /** Whether a record opened from here shows in the list's pane rather than in the side peek. */
    function opensInPane(): boolean {
        return !isInsideInspector && paneNavigator !== null && paneNavigator.isActive();
    }

    /**
     * Shows a record where this screen shows records. Inside a peek the peek
     * moves along its trail. On a list, from the pane or from one of its rows,
     * the pane moves. Anywhere else the record opens as the side peek.
     */
    function openRecord(
        entityId: string,
        recordId: number | string,
        options: OpenRecordOptions = {}
    ): void {
        if (paneNavigator && opensInPane()) {
            paneNavigator.showRecord(entityId, recordId, options.initialSection ? { initialSection: options.initialSection } : {});
            return;
        }
        const overlayHeader = overlayHeaderComponentFor(entityId);
        const shouldPush =
            isInsideInspector ||
            (options.pushWhenInspectorOpen === true && inspectorStore.isOpen);
        const openView = shouldPush
            ? inspectorStore.push.bind(inspectorStore)
            : inspectorStore.open.bind(inspectorStore);
        // Friendly fallback title ("Custom Playlist #1", not "LocationZoneCustomPlaylist #1");
        // the overlay header swaps in the record's display_string once it's fetched.
        openView(
            `${entityDisplayNameFor(entityId)} #${recordId}`,
            markRaw(ModelRecordInspector),
            // `initialSection` only appears when a caller asked for one. Passing
            // it unconditionally put a null on every record any surface opens,
            // which is noise in the props of every inspector that has no
            // sections at all.
            {
                entityId,
                recordId,
                ...(options.initialSection ? { initialSection: options.initialSection } : {}),
            },
            {
                ...(overlayHeader
                    ? { headerComponent: markRaw(overlayHeader), headerProps: { entityId, recordId } }
                    : {}),
                ...(options.originTitle ? { originTitle: options.originTitle } : {}),
            }
        );
    }

    return { canOpen, openRecord, opensInPane, isInsideInspector };
}
